import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import AuthLayout from './components/AuthLayout';
import SiteGate from './components/SiteGate';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import ServiceRequestPage from './pages/ServiceRequestPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import ProviderProfilePage from './pages/ProviderProfilePage';
import ProviderServicesPage from './pages/ProviderServicesPage';
import ServiceCatalogPage from './pages/ServiceCatalogPage';
import AdminManagementPage from './pages/AdminManagementPage';
import AdminCategoriesPage from './pages/AdminCategoriesPage';
import AdminCategoryEditPage from './pages/AdminCategoryEditPage';
import ProviderVerificationPage from './pages/ProviderVerificationPage';
import ClientVerificationPage from './pages/ClientVerificationPage';
import CatalogPage from './pages/CatalogPage';
import ContactPage from './pages/ContactPage';
import HowToUsePage from './pages/HowToUsePage';
import './i18n';
import './App.css';

// ── ProtectedRoute — redirect to /auth if not logged in ──────────────────────
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  return user ? children : <Navigate to="/auth" />;
};

// ── AuthRoute — protected + shared nav layout ────────────────────────────────
const AuthRoute = ({ children }) => (
  <ProtectedRoute>
    <AuthLayout>
      {children}
    </AuthLayout>
  </ProtectedRoute>
);

// ── AdminRoute — redirects non-admins to /dashboard ─────────────────────────
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  if (!user) return <Navigate to="/auth" />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <SiteGate>
            <Routes>
              {/* Public routes — no shared nav */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/how-to-use" element={<HowToUsePage />} />

              {/* Admin routes — have their own nav, not wrapped */}
              <Route
                path="/admin-management"
                element={
                  <AdminRoute>
                    <AdminManagementPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/categories"
                element={
                  <AdminRoute>
                    <AdminCategoriesPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/categories/:id"
                element={
                  <AdminRoute>
                    <AdminCategoryEditPage />
                  </AdminRoute>
                }
              />

              {/* Authenticated routes — wrapped with AuthLayout */}
              <Route path="/dashboard" element={<AuthRoute><Dashboard /></AuthRoute>} />
              <Route path="/service-request" element={<AuthRoute><ServiceRequestPage /></AuthRoute>} />
              <Route path="/service/:requestId" element={<AuthRoute><ServiceDetailPage /></AuthRoute>} />
              <Route path="/provider/:providerId" element={<AuthRoute><ProviderProfilePage /></AuthRoute>} />
              <Route path="/provider-profile" element={<AuthRoute><ProviderProfilePage /></AuthRoute>} />
              <Route path="/provider-services" element={<AuthRoute><ProviderServicesPage /></AuthRoute>} />
              <Route path="/provider/verification" element={<AuthRoute><ProviderVerificationPage /></AuthRoute>} />
              <Route path="/verify" element={<AuthRoute><ClientVerificationPage /></AuthRoute>} />
              <Route path="/catalog" element={<AuthRoute><CatalogPage /></AuthRoute>} />
              <Route path="/catalog/:categorySlug" element={<AuthRoute><ServiceCatalogPage /></AuthRoute>} />
              <Route path="/catalog/:categorySlug/:subcategorySlug" element={<AuthRoute><ServiceCatalogPage /></AuthRoute>} />
            </Routes>
          </SiteGate>
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
